import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { WhatsAppBroadcastingState } from 'apps/whatsApp/interfaces/whatsAppBroadCast'
import { Filter } from 'common/filter/interfaces'
import { WhatsAppApi } from 'shared/api/requests/whatsApp'
import {
  WaBroadcastCostCategory,
  WhatsAppBroadcastingBicLimits,
  WhatsAppBroadcastingCostEstimation,
} from 'shared/api/requests/whatsApp/schemas'
import { createAsyncAction } from 'shared/lib/redux/createAsyncAction'

export const getBicLimits = createAsyncAction<WhatsAppBroadcastingBicLimits | null>(
  'whatsapp/bicLimits',
  async () => {
    const result = await WhatsAppApi.getBicLimits()
    return result.data
  },
  {
    // We don't want an error toast message to be displayed for this error
    onBusinessError: (error) => {
      if (WhatsAppApi.getBicLimits.isProError(error)) {
        error.handle()
      }
    },
  },
)

export const estimateBroadcastCost = createAsyncAction<
  WhatsAppBroadcastingCostEstimation | null,
  { category: WaBroadcastCostCategory; filter: Filter }
>(
  'whatsapp/estimateBroadcastCost',
  async ({ category, filter }) => {
    const result = await WhatsAppApi.estimateBroadcastCost({ body: { category, filter } })
    return result.data
  },
  {
    // We don't want an error toast message to be displayed for this error
    onBusinessError: (error) => {
      if (WhatsAppApi.estimateBroadcastCost.isProError(error)) {
        error.handle()
      }
    },
  },
)

const initialState: WhatsAppBroadcastingState = {
  bicLimits: {
    data: null,
    loading: false,
  },
  costEstimation: {
    data: null,
    loading: false,
    isComplexBroadcast: false,
    isEstimationFailed: false,
  },
}

export const broadcastingSlice = createSlice({
  name: 'broadcasting',
  initialState: initialState,
  reducers: {
    setIsCostEstimationPending: (state, action: PayloadAction<boolean>) => {
      state.costEstimation.loading = action.payload
    },
    resetCostEstimation: (state) => {
      state.costEstimation.data = null
    },
    setIsComplexBroadcast: (state) => {
      state.costEstimation.isComplexBroadcast = true
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBicLimits.pending, (state) => {
      state.bicLimits.loading = true
    })
    builder.addCase(getBicLimits.fulfilled, (state, action) => {
      state.bicLimits.loading = false
      state.bicLimits.data = action.payload
    }),
      builder.addCase(estimateBroadcastCost.pending, (state) => {
        state.costEstimation.loading = true
        state.costEstimation.isComplexBroadcast = false
        state.costEstimation.isEstimationFailed = false
      })
    builder.addCase(estimateBroadcastCost.fulfilled, (state, action) => {
      state.costEstimation.loading = false
      state.costEstimation.isComplexBroadcast = false
      state.costEstimation.isEstimationFailed = false
      state.costEstimation.data = action.payload
    })
    builder.addCase(estimateBroadcastCost.rejected, (state) => {
      state.costEstimation.loading = false
      state.costEstimation.isEstimationFailed = true
      state.costEstimation.data = null
    })
  },
})

export const { setIsCostEstimationPending, resetCostEstimation, setIsComplexBroadcast } =
  broadcastingSlice.actions

export const getWaBroadcastingBicLimitsDataSelector = (state: RootState) =>
  state.whatsApp.broadcasting.bicLimits.data
export const getIsWaBroadcastingBicLimitsLoadingSelector = (state: RootState) =>
  state.whatsApp.broadcasting.bicLimits.loading
export const getWaBroadcastingCostEstimationSelector = (state: RootState) =>
  state.whatsApp.broadcasting.costEstimation.data
export const getWaBroadcastingCostEstimationIsComplex = (state: RootState) =>
  state.whatsApp.broadcasting.costEstimation.isComplexBroadcast
export const getWaBroadcastingCostEstimationFailed = (state: RootState) =>
  state.whatsApp.broadcasting.costEstimation.isEstimationFailed
export const getIsWaBroadcastingCostEstimationLoadingSelector = (state: RootState) =>
  state.whatsApp.broadcasting.costEstimation.loading
